import { datadogRum } from '@datadog/browser-rum';
import { ReloadableResultStatus } from 'api/resultStatus';
import React, { useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';

import {
  defaultRichTextElements,
  LanguageProviderProps,
  mapLocaleCodes,
  Translations,
} from './shared';
const translationPromise = import('../messages');

const RTL_LOCALES = ['he'];

type LoadingTranslations = ReloadableResultStatus<Translations>;

/**
 * LanguageProvider
 *
 * This component uses the locale from props and connects it to the
 * IntlProvider component and i18n messages (loaded from `app/translations`)
 */
export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
  locale,
  timeZone,
}) => {
  const mappedLocale = mapLocaleCodes(locale);
  const [messages, setMessages] = useState<LoadingTranslations>({
    isLoading: true,
    isError: false,
    status: 'loading',
  });
  useEffect(() => {
    translationPromise
      .then(data =>
        setMessages({
          isLoading: false,
          isError: false,
          status: 'ready',
          data: data.translationMessages,
        }),
      )
      .catch(err => {
        datadogRum.addError(err);
        setMessages({
          isLoading: false,
          isError: true,
          status: 'error',
        });
      });
  }, []);

  useEffect(() => {
    if (RTL_LOCALES.includes(locale)) {
      document.body.setAttribute('dir', 'rtl');
    }
  }, [locale]);

  // the promise is lazy loading the translation file. We don't want to evaluate children
  // since it will rely on the translations being ready
  if (messages.status !== 'ready') {
    return null;
  }

  return (
    <IntlProvider
      locale={mappedLocale}
      key={mappedLocale}
      messages={messages.data[mappedLocale]}
      textComponent={React.Fragment}
      defaultRichTextElements={defaultRichTextElements}
      timeZone={timeZone}
    >
      {React.Children.only(children)}
    </IntlProvider>
  );
};
